'use client';
import React, { useCallback, useEffect } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';

interface Props {
  classes?: any;
  title: string;
  children: React.ReactNode;
  titleVariant?: React.ElementType;
  titleClassName?: string;
  onClick?: () => void;
  className?: string;
  dark?: boolean;
  isOpen?: boolean;
  controlled?: boolean;
}

const BaseAccordion = ({
  classes,
  title,
  children,
  titleVariant: TitleTag = 'h5',
  titleClassName,
  onClick,
  className,
  dark = false,
  isOpen = false,
  controlled = false,
}: Props) => {
  const [expanded, setExpanded] = React.useState(!!isOpen);

  useEffect(() => {
    setExpanded(!!isOpen);
  }, [isOpen]);

  const internalOnClick = useCallback(() => {
    onClick?.();
    if (!controlled) {
      setExpanded((ex) => !ex);
    }
  }, [onClick, controlled]);

  const textColor = dark ? 'text-neutral-50' : 'text-neutral-500';

  const slug = title.replace(/[^\w-]+/g, '-');
  const triggerId = slug;
  const contentId = `${slug}-content`;

  return (
    <div
      className={classNames(
        'border-t border-neutral-200 transition-all duration-300',
        expanded ? 'pb-4' : 'pb-0',
        className || classes?.accordion,
      )}
    >
      <TitleTag className={classNames('text-left', textColor, titleClassName)}>
        <Button
          variant={ButtonVariant.bare}
          size="none"
          className={classNames(
            'flex justify-between items-center py-3 cursor-pointer w-full',
            classes?.accordionSummary,
            expanded && classes?.accordionSummaryExpanded,
          )}
          onClick={internalOnClick}
          aria-controls={contentId}
          id={triggerId}
          role="button"
          aria-expanded={expanded}
        >
          {title}

          <ChevronIcon
            className={classNames(
              'w-6 h-6 max-w-5 transform transition-transform duration-300',
              expanded ? 'rotate-0' : 'rotate-180',
              textColor,
            )}
            title={`Chevron-${title}`}
          />
        </Button>
      </TitleTag>

      <div
        className={classNames(
          'overflow-hidden transition-all duration-300 pl-1',
          expanded ? 'max-h-screen opacity-100 py-3' : 'max-h-0 opacity-0 pointer-events-none',
          classes?.accordionDetails,
        )}
        id={contentId}
        style={{ visibility: !expanded ? 'hidden' : 'visible' }}
        aria-hidden={!expanded}
      >
        {children}
      </div>
    </div>
  );
};

export default BaseAccordion;
